import { useEffect } from "react";

import type {
	FlashcardSetPopulated,
	GistPopulated,
	NotePopulated,
	QuizPopulated,
	UserDetails,
} from "@vita-learning/types";

import { useFlashcardSetsStore } from "~/client/components/stores/FlashcardSetsStoreProvider";
import { useGistsStore } from "~/client/components/stores/GistsStoreProvider";
import { useNotesStore } from "~/client/components/stores/NotesStoreProvider";
import { useQuizzesStore } from "~/client/components/stores/QuizzesStoreProvider";
import { useLocalSpace } from "~/client/hooks/useLocalSpace";

const LOCALSPACE_ARGS = [null, { initializeWithValue: false }] as const;

export const useImportPreviewResource = () => {
	const [note, _, removeNote] = useLocalSpace<NotePopulated>(
		"preview_note",
		...LOCALSPACE_ARGS
	);
	const [gist, __, removeGist] = useLocalSpace<GistPopulated>(
		"preview_gist",
		...LOCALSPACE_ARGS
	);
	const [flashcardSet, ___, removeFlashcardSet] =
		useLocalSpace<FlashcardSetPopulated>(
			"preview_flashcards",
			...LOCALSPACE_ARGS
		);
	const [quiz, ____, removeQuiz] = useLocalSpace<QuizPopulated>(
		"preview_quiz",
		...LOCALSPACE_ARGS
	);

	const createNote = useNotesStore(s => s.create);
	const createGist = useGistsStore(s => s.create);
	const createFlashcardSet = useFlashcardSetsStore(s => s.create);
	const createQuiz = useQuizzesStore(s => s.create);

	const importPreviewResource = async (user: UserDetails) => {
		const results = await Promise.allSettled([
			note
				? createNote({
						userId: user.id,
						title: note.title,
						content: note.content,
						source: "share",
						shareId: note.id,
						tags: note.tags,
					})
				: Promise.resolve(null),
			gist
				? createGist({
						userId: user.id,
						title: gist.title,
						content: gist.content,
						tags: gist.tags,
						source: "share",
						shareId: gist.id,
						originalContent: gist.content,
						keywords: gist.keywords,
					})
				: Promise.resolve(null),
			flashcardSet
				? createFlashcardSet({
						userId: user.id,
						title: flashcardSet.title,
						cards: flashcardSet.cards,
						description: flashcardSet.description,
						tags: flashcardSet.tags,
						date: flashcardSet.date,
						source: "share",
						shareId: flashcardSet.id,
					})
				: Promise.resolve(null),
			quiz
				? createQuiz({
						userId: user.id,
						title: quiz.title,
						questions: quiz.questions,
						format: quiz.format,
						tags: quiz.tags,
						date: quiz.date,
						source: "share",
						shareId: quiz.id,
					})
				: Promise.resolve(null),
		]);

		for (const result of results)
			if (result.status === "rejected") console.error(result.reason);
	};

	useEffect(() => {
		return () => {
			removeNote();
			removeGist();
			removeFlashcardSet();
			removeQuiz();
		};
	}, []);

	return importPreviewResource;
};
